import { useRef } from "react";
import { useTranslation } from "react-i18next";
import { IconSearch, IconX } from "@tabler/icons-react";
import { cn } from "../../../lib/utils";

// Search field that sits in the onboarding footer (over the frosted shade) so
// the selectable list scrolls behind it. Shared by the country, profession,
// specialty and practice steps, and by the settings sheets that edit the same
// lists; the placeholder is passed already-translated so the i18n scanner
// extracts each literal at the call site.
export function StepSearchInput({
  value,
  onChange,
  placeholder,
  variant,
}: {
  value: string;
  onChange: (value: string) => void;
  placeholder: string;
  /**
   * `mobile` is the frosted field over the carousel's lists. `desktop` is the
   * desktop onboarding's (Figma "D7.pratiques.liste"): a white field, the
   * magnifier leading.
   */
  variant: "mobile" | "desktop";
}) {
  const { t } = useTranslation();
  const inputRef = useRef<HTMLInputElement | null>(null);

  return (
    <div
      className={cn(
        "flex items-center gap-2 px-4",
        variant === "mobile"
          ? "rounded-2xl border border-ds-slate-500/20 bg-ds-slate-50 py-3.5 shadow-md"
          : "h-12 rounded-xl border border-ds-black/6 bg-ds-white"
      )}
    >
      {variant === "desktop" && (
        <IconSearch aria-hidden className="size-4 shrink-0 text-ds-slate-500" />
      )}
      <input
        ref={inputRef}
        type="text"
        className="min-w-0 flex-1 bg-transparent font-dm-sans text-ui-body text-primary-black placeholder:text-ds-slate-500 focus:outline-none"
        placeholder={placeholder}
        // The practice slide sends what is typed here as a declaration, and the
        // API caps that at 120 characters. Without the cap the row offers to
        // send words the PUT then refuses, and signup reports nothing back.
        // No search term of any other step comes close to this length.
        maxLength={120}
        value={value}
        onChange={(e) => {
          onChange(e.target.value);
        }}
      />
      {value.length === 0 ? (
        variant === "mobile" && (
          <IconSearch className="size-5 shrink-0 text-ds-slate-500" />
        )
      ) : (
        <button
          type="button"
          aria-label={t("Clear search")}
          // The tap target is 32px, but the negative margins let it overflow
          // the row down to the 20px the magnifier occupies, so the field keeps
          // one height whether or not it holds text.
          className="-my-1.5 -mr-1.5 flex size-8 shrink-0 touch-manipulation items-center justify-center rounded-full text-ds-slate-500 hover:text-ds-black"
          // The field lives under the mobile keyboard: let the button take the
          // focus and the keyboard collapses under the thumb that cleared.
          onPointerDown={(e) => {
            e.preventDefault();
          }}
          onClick={() => {
            onChange("");
            const input = inputRef.current;
            if (input === null)
              throw new Error("should not happen: cleared an unmounted field");
            input.focus();
          }}
        >
          <IconX className="size-4" />
        </button>
      )}
    </div>
  );
}
